import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import usePersistentState from "@/hooks/usePersistentState";
import UserDetailModal from "./UserDetailModal";
import BulkRateModal from "./BulkRateModal";
import BulkStatusModal from "./BulkStatusModal";
import BulkAgencyModal from "./BulkAgencyModal";
import CreditAdjustModal from "./CreditAdjustModal";

interface User {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  plan: string;
  status: string;
  credit: number;
  created_at: string;
  total_sent: number;
  total_charged: number;
  per_message_rate: number | null;
  is_agency: boolean;
  parent_agency_id: string | null;
  referral_code: string | null;
}

interface Agency {
  id: string;
  full_name: string | null;
  email: string | null;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

// 탭 복귀(리로드) 시 로딩 없이 마지막 목록을 즉시 보여주기 위한 캐시
const USERS_CACHE_KEY = "cache:admin_users";

function readUsersCache(): User[] | null {
  try {
    const raw = sessionStorage.getItem(USERS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as User[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeUsersCache(list: User[]): void {
  try {
    sessionStorage.setItem(USERS_CACHE_KEY, JSON.stringify(list));
  } catch {
    // 캐시 저장 실패는 무시
  }
}

const PLAN_OPTIONS = ["전체", "FREE", "BASIC", "PRO", "ENTERPRISE"];

const PLAN_COLORS: Record<string, string> = {
  FREE: "bg-gray-500/20 text-gray-400",
  BASIC: "bg-sky-500/20 text-sky-400",
  PRO: "bg-violet-500/20 text-violet-400",
  ENTERPRISE: "bg-rose-500/20 text-rose-400",
};

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-500/20 text-emerald-400",
  inactive: "bg-gray-500/20 text-gray-400",
  banned: "bg-red-500/20 text-red-400",
  pending: "bg-amber-500/20 text-amber-400",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

export default function UserManagementTab() {
  const { session } = useAuth();
  const cachedUsersRef = useRef<User[] | null>(readUsersCache());
  const [users, setUsers] = useState<User[]>(cachedUsersRef.current ?? []);
  const [loading, setLoading] = useState(cachedUsersRef.current === null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = usePersistentState("admin_users_search", "");
  const [planFilter, setPlanFilter] = usePersistentState("admin_users_plan", "전체");
  const [page, setPage] = usePersistentState("admin_users_page", 1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkRate, setShowBulkRate] = useState(false);
  const [showBulkStatus, setShowBulkStatus] = useState(false);
  const [showBulkAgency, setShowBulkAgency] = useState(false);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [creditAdjustUser, setCreditAdjustUser] = useState<User | null>(null);
  const PER_PAGE = 10;

  const fetchUsers = useCallback(async () => {
    const accessToken = session?.access_token;
    if (!accessToken) return;
    // 캐시된 목록이 이미 있으면 로딩 화면 없이 조용히 갱신
    if (cachedUsersRef.current === null) setLoading(true);
    setError(null);
    try {
      const [usersRes, agenciesRes] = await Promise.all([
        fetch(`${SUPABASE_URL}/functions/v1/get-admin-users`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            apikey: SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
          },
        }),
        fetch(`${SUPABASE_URL}/functions/v1/get-admin-agencies`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            apikey: SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
          },
        }),
      ]);

      if (!usersRes.ok) {
        const err = await usersRes.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${usersRes.status}`);
      }

      const usersJson = await usersRes.json();
      const agenciesJson = await agenciesRes.json();

      const data = (usersJson.users ?? []).map((u: any) => ({
        id: u.id,
        full_name: u.full_name || u.name || null,
        email: u.email || null,
        phone: u.phone || null,
        plan: u.plan || "FREE",
        status: u.status || "active",
        credit: u.credit || 0,
        created_at: u.created_at,
        total_sent: u.total_sent || 0,
        total_charged: u.total_charged || 0,
        per_message_rate: u.per_message_rate ?? null,
        is_agency: u.is_agency ?? false,
        referral_code: u.referral_code ?? null,
      }));
      setUsers(data);
      writeUsersCache(data);
      setAgencies(agenciesJson.agencies ?? []);
      // Update selected user if modal is open
      setSelectedUser((prev) => {
        if (!prev) return null;
        const updated = data.find((u: User) => u.id === prev.id);
        return updated ?? null;
      });
    } catch (err: any) {
      console.error("[UserManagement] fetch error:", err);
      setError(err.message || "회원 목록을 불러오는 중 오류가 발생했습니다.");
    } finally {
      cachedUsersRef.current = [];
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filtered = useMemo(() => {
    let result = [...users];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (u) =>
          (u.full_name?.toLowerCase() ?? "").includes(q) ||
          (u.email?.toLowerCase() ?? "").includes(q) ||
          (u.phone ?? "").includes(q)
      );
    }
    if (planFilter !== "전체") {
      result = result.filter((u) => u.plan === planFilter);
    }
    return result;
  }, [users, search, planFilter]);

  // 필터가 실제로 바뀌면 첫 페이지로 (최초 복원 시에는 페이지 유지)
  const skipResetRef = useRef(true);
  useEffect(() => {
    if (skipResetRef.current) {
      skipResetRef.current = false;
      return;
    }
    setPage(1);
    // 필터가 바뀌면 선택도 초기화
    setSelectedIds(new Set());
  }, [search, planFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // Selection helpers
  const pagedIds = paged.map((u) => u.id);
  const isAllSelectedOnPage = pagedIds.length > 0 && pagedIds.every((id) => selectedIds.has(id));
  const isSomeSelectedOnPage = pagedIds.some((id) => selectedIds.has(id));

  const handleSelectAll = useCallback(() => {
    if (isAllSelectedOnPage) {
      // Deselect all on current page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pagedIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      // Select all on current page
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pagedIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [isAllSelectedOnPage, pagedIds]);

  const handleSelectOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectedCount = selectedIds.size;
  const selectedUsers = users.filter((u) => selectedIds.has(u.id));

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-rose-500/40 border-t-rose-500 rounded-full animate-spin mb-3"></div>
        <p className="text-gray-500 text-sm">회원 목록 불러오는 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <i className="ri-error-warning-line text-3xl text-red-400 mb-2 block"></i>
        <p className="text-red-400 text-sm mb-3">{error}</p>
        <button
          onClick={fetchUsers}
          className="px-4 py-2 bg-white/[0.06] hover:bg-white/[0.1] text-gray-300 rounded-lg text-sm cursor-pointer transition-colors"
        >
          다시 시도
        </button>
      </div>
    );
  }

  const agencyName = (agencyId: string | null) => {
    if (!agencyId) return null;
    const a = agencies.find((ag) => ag.id === agencyId);
    return a?.full_name || a?.email || "알 수 없음";
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-white font-semibold text-lg">회원 관리</h2>
          <p className="text-gray-500 text-sm mt-0.5">총 {users.length.toLocaleString()}명의 회원이 등록되어 있습니다</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchUsers}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white cursor-pointer transition-colors"
            title="새로고침"
          >
            <i className="ri-refresh-line text-sm"></i>
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="이름, 이메일, 전화번호 검색"
            className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-rose-500/40 transition-colors"
          />
        </div>
        <div className="flex items-center bg-white/[0.04] border border-white/[0.08] rounded-lg p-0.5 gap-0.5">
          {PLAN_OPTIONS.map((p) => (
            <button
              key={p}
              onClick={() => setPlanFilter(p)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                planFilter === p ? "bg-rose-600 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk toolbar */}
      {selectedCount > 0 && (
        <div className="flex items-center justify-between bg-rose-500/10 border border-rose-500/20 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2">
            <i className="ri-checkbox-multiple-line text-rose-400 text-sm"></i>
            <span className="text-white text-sm font-medium">
              {selectedCount.toLocaleString()}명 선택됨
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.06] text-xs font-medium cursor-pointer transition-colors"
            >
              선택 해제
            </button>
            <button
              onClick={() => setShowBulkStatus(true)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-shield-user-line mr-1"></i>
              일괄 상태 변경
            </button>
            <button
              onClick={() => setShowBulkRate(true)}
              className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-coins-line mr-1"></i>
              일괄 가격 설정
            </button>
            <button
              onClick={() => setShowBulkAgency(true)}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-building-4-line mr-1"></i>
              에이전시 배정
            </button>
          </div>
        </div>
      )}

      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/[0.08]">
                <th className="px-4 py-3 w-10">
                  <button
                    onClick={handleSelectAll}
                    className="w-5 h-5 flex items-center justify-center rounded border border-white/[0.15] cursor-pointer transition-colors"
                    aria-label="전체 선택"
                  >
                    {isAllSelectedOnPage ? (
                      <i className="ri-checkbox-fill text-rose-400 text-sm"></i>
                    ) : isSomeSelectedOnPage ? (
                      <i className="ri-checkbox-indeterminate-fill text-rose-400 text-sm"></i>
                    ) : (
                      <i className="ri-checkbox-blank-line text-gray-600 text-sm"></i>
                    )}
                  </button>
                </th>
                {["회원", "플랜", "상태", "크레딧", "단가", "소속 에이전시", "가입일", "관리"].map((h) => (
                  <th key={h} className="px-4 py-3 text-gray-500 text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500 text-sm">
                    조건에 맞는 회원이 없습니다
                  </td>
                </tr>
              ) : (
                paged.map((u) => {
                  const displayName = u.full_name || u.email || "이름 없음";
                  const isChecked = selectedIds.has(u.id);
                  const hasRate = u.per_message_rate != null;
                  const agency = agencyName(u.parent_agency_id);
                  return (
                    <tr key={u.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleSelectOne(u.id)}
                          className="w-5 h-5 flex items-center justify-center rounded border border-white/[0.15] cursor-pointer transition-colors"
                          aria-label="회원 선택"
                        >
                          {isChecked ? (
                            <i className="ri-checkbox-fill text-rose-400 text-sm"></i>
                          ) : (
                            <i className="ri-checkbox-blank-line text-gray-600 text-sm"></i>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-orange-500 text-white text-xs font-semibold shrink-0">
                            {displayName.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-white text-sm font-medium truncate">{displayName}</p>
                            <p className="text-gray-500 text-xs truncate">{u.email || "-"}</p>
                            {u.phone && <p className="text-gray-600 text-[11px]">{u.phone}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLAN_COLORS[u.plan] ?? PLAN_COLORS.FREE}`}>
                          {u.plan || "FREE"}
                        </span>
                        {u.is_agency && (
                          <span className="ml-1 text-xs px-2 py-0.5 rounded-full font-medium bg-amber-500/10 text-amber-400">
                            에이전시
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[u.status] ?? STATUS_COLORS.active}`}>
                          {u.status || "active"}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-white text-sm font-medium">
                        ₩{(u.credit || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {hasRate ? (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-500/10 text-amber-400">
                            ₩{u.per_message_rate?.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-500/10 text-gray-500">
                            기본
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {agency ? (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-500/10 text-amber-400 whitespace-nowrap">
                            <i className="ri-building-4-line mr-1 text-[10px]"></i>
                            {agency}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                        {formatDate(u.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setCreditAdjustUser(u)}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 hover:bg-amber-500/20 text-xs font-medium cursor-pointer transition-colors whitespace-nowrap"
                            title="크레딧 조정"
                          >
                            <i className="ri-coins-line text-sm"></i>
                            조정
                          </button>
                          <button
                            onClick={() => setSelectedUser(u)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white hover:bg-white/[0.1] text-xs font-medium cursor-pointer transition-colors"
                          >
                            <i className="ri-eye-line text-sm"></i>
                            보기
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 py-3 border-t border-white/[0.08]">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line text-sm"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-semibold cursor-pointer transition-all ${
                  p === page
                    ? "bg-rose-500/20 border border-rose-500/30 text-rose-400"
                    : "text-gray-500 hover:text-white hover:bg-white/[0.05]"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line text-sm"></i>
            </button>
          </div>
        )}
      </div>

      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUpdated={fetchUsers}
        />
      )}

      {showBulkRate && (
        <BulkRateModal
          selectedUsers={selectedUsers}
          onClose={() => setShowBulkRate(false)}
          onSaved={() => {
            setSelectedIds(new Set());
            fetchUsers();
          }}
        />
      )}

      {showBulkStatus && (
        <BulkStatusModal
          selectedUsers={selectedUsers}
          onClose={() => setShowBulkStatus(false)}
          onSaved={() => {
            setSelectedIds(new Set());
            fetchUsers();
          }}
        />
      )}

      {showBulkAgency && (
        <BulkAgencyModal
          selectedUsers={selectedUsers}
          onClose={() => setShowBulkAgency(false)}
          onSaved={() => {
            setSelectedIds(new Set());
            fetchUsers();
          }}
        />
      )}

      {/* Credit Adjust Modal from list */}
      {creditAdjustUser && (
        <CreditAdjustModal
          user={{
            id: creditAdjustUser.id,
            full_name: creditAdjustUser.full_name,
            email: creditAdjustUser.email,
            credit: creditAdjustUser.credit,
          }}
          onClose={() => setCreditAdjustUser(null)}
          onSuccess={() => {
            fetchUsers();
          }}
        />
      )}
    </div>
  );
}